/**
 * ThinkingPulse — inline "agent is at work" indicator.
 *
 * Two layers, no extra orb here (the parent renders the small DysonSphere
 * mini-orb alongside this; we just provide the kinetic text):
 *
 *   1. A "scanning" highlight pass that sweeps left → right over the
 *      verb itself — like a thin beam wiping across the word. CSS-only,
 *      uses ``background-clip: text`` with an animated linear gradient
 *      that travels across the glyph. No extra DOM nodes per character.
 *
 *   2. A cycling verb that changes every ~1.4 s. The list intentionally
 *      runs 24 deep with verbs that feel evocative rather than generic
 *      ("incubating", "marinating", "germinating", …) so a long pause
 *      while the agent thinks reads as deliberate work, not a stall.
 *
 * Tokens-only, square corners, monospace, no inline SVG — same design
 * constraints as the rest of ``Docs/DESIGN.md``. Keyframe lives in
 * ``dashboard/src/styles/globals.css`` (``om-text-sweep``).
 */
import * as React from "react";
import { cn } from "@/lib/cn";

// 24 verbs — deliberately diverse so the user sees real variety even on
// a 30-second think. Mixed register (computational + organic) so the
// agent feels like it's *working*, not just spinning a wheel.
const WORDS = [
  "thinking",
  "reasoning",
  "considering",
  "exploring",
  "synthesizing",
  "weaving",
  "incubating",
  "brewing",
  "conjuring",
  "distilling",
  "untangling",
  "marinating",
  "ruminating",
  "computing",
  "dreaming",
  "unfolding",
  "pondering",
  "puzzling",
  "decoding",
  "cooking",
  "forging",
  "germinating",
  "calibrating",
  "discovering",
];
const CYCLE_MS = 1400;

export interface ThinkingPulseProps {
  className?: string;
}

export function ThinkingPulse({ className }: ThinkingPulseProps) {
  // Pick a random starting index per mount so two thinking sessions in
  // a row don't always open with "thinking…" — they feel different.
  const [i, setI] = React.useState(() =>
    Math.floor(Math.random() * WORDS.length),
  );
  React.useEffect(() => {
    const id = window.setInterval(
      () => setI((v) => (v + 1) % WORDS.length),
      CYCLE_MS,
    );
    return () => window.clearInterval(id);
  }, []);

  const word = WORDS[i];

  return (
    <span
      className={cn(
        "inline-flex items-baseline text-sm font-mono text-fg-muted",
        className,
      )}
      aria-live="polite"
      aria-label={`Agent is ${word}`}
    >
      {/* Outer keyed wrapper so React re-mounts on each verb change —
          the fade-in restarts cleanly between words. */}
      <span key={word} className="om-fade-in">
        {/* The "scanned" word: the text itself is dim (fg-muted), and an
            animated bright accent sweep passes over it. Implemented with
            ``background-clip: text`` so the highlight is constrained to
            the glyph outline, not a separate overlay. */}
        <span className="om-text-sweep">{word}</span>
        {/* Trailing ellipsis stays static — only the verb is "scanned". */}
        <span className="ml-px">…</span>
      </span>
    </span>
  );
}
